/**
 * Converts an unknown caught value into a plain string for logging.
 *
 * Shared by both the browser (`exception-logging.ts`) and SSR (`server.ts`)
 * entry points, and re-exported from `@hungryroot/payments`'s
 * `serializeLogError` for the same purpose — never pass a raw Error/object as
 * a log data value, since Datadog attributes are flattened and an
 * un-serialized object renders as "[object Object]" instead of the reason
 * for the failure.
 */
export function getStringError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;

  try {
    // JSON.stringify returns `undefined` (not a string) for `undefined`,
    // functions, and symbols — fall back to String() so those still
    // serialize instead of silently dropping the attribute.
    return JSON.stringify(error, getCircularReplacer()) ?? String(error);
  } catch {
    // JSON.stringify can still throw (e.g. BigInt values), even with the
    // circular-reference replacer above.
    return String(error);
  }
}

// A repeated object reference (e.g. a circular structure) would otherwise
// throw out of JSON.stringify entirely, which falls through to String() and
// renders as "[object Object]" — replace repeats with a marker instead so
// the rest of the object still serializes. See:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#examples
function getCircularReplacer() {
  const seen = new WeakSet();
  return (_key: string, value: unknown) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  };
}
